import { Component, EventEmitter, Input } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { Brand } from 'src/typings';
import { BrandService } from '../brand.service';

type OccupiedBrandConflict = {
  id: number;
  description: string;
  shortDescription?: string | null;
  productCount?: number;
  message: string;
};

@Component({
  selector: 'app-add-another-description',
  templateUrl: './add-another-description.component.html',
  styleUrls: ['./add-another-description.component.scss']
})
export class AddAnotherDescriptionComponent {
  @Input() brand: Brand | undefined;

  descriptionControl: UntypedFormControl = new UntypedFormControl();

  doneEvent: EventEmitter<Brand> = new EventEmitter();

  loading = false;

  occupiedBrandConflict: OccupiedBrandConflict | null = null;

  constructor(
    private brandService: BrandService,
    private notificationService: NotificationService
  ) {}

  async save() {
    if (!this.brand) return window.alert('Нет производителя!');
    if (this.loading) return;

    this.occupiedBrandConflict = null;
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.brandService.addAnotherBrandDescription({
          id: this.brand.id,
          description: this.descriptionControl.value
        })
      );
      this.doneEvent.emit(res);
      this.notificationService.notice('Маппинг сохранён.');
    } catch (err) {
      const conflict = await this.extractOccupiedBrandConflict(err);
      if (conflict) {
        this.occupiedBrandConflict = conflict;
        return;
      }
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось сохранить маппинг бренда')
      );
    } finally {
      this.loading = false;
    }
    return;
  }

  async mergeConflictingBrand() {
    if (!this.brand || !this.occupiedBrandConflict) return;
    const confirmed = window.confirm(
      `Слить бренд "${this.occupiedBrandConflict.description}" (#${this.occupiedBrandConflict.id}) в "${this.brand.description}" (#${this.brand.id})? Все товары и маппинги будут перенесены в текущий бренд.`
    );
    if (!confirmed) return;

    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.brandService.merge({
          main_id: this.brand.id,
          another_id: this.occupiedBrandConflict.id
        })
      );
      this.notificationService.notice(
        `Бренд "${this.occupiedBrandConflict.description}" слит в "${res.description}".`
      );
      this.occupiedBrandConflict = null;
      this.doneEvent.emit(res);
    } catch (err) {
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось слить бренды')
      );
    } finally {
      this.loading = false;
    }
  }

  private async extractOccupiedBrandConflict(err: any): Promise<OccupiedBrandConflict | null> {
    const payload = err?.error;
    if (payload?.code !== 'BRAND_MAPPING_OCCUPIED' || !payload?.conflictingBrand?.id) {
      return this.extractOccupiedBrandConflictFromMessage(err);
    }

    return {
      id: Number(payload.conflictingBrand.id),
      description: String(payload.conflictingBrand.description || ''),
      shortDescription: payload.conflictingBrand.shortDescription || null,
      productCount: Number(payload.conflictingBrand.productCount) || 0,
      message: String(payload.message || '')
    };
  }

  private async extractOccupiedBrandConflictFromMessage(
    err: any
  ): Promise<OccupiedBrandConflict | null> {
    const rawMessage = typeof err === 'string' ? err : extractApiErrorMessage(err, '');
    if (!rawMessage || !rawMessage.includes('уже привязан к бренду')) {
      return null;
    }

    const match = rawMessage.match(/к бренду "(.+)" \(#(\d+)\)/);
    if (!match) {
      return null;
    }

    const conflictingBrandId = Number(match[2]);
    const conflictingBrandDescription = match[1];

    try {
      const brand = await firstValueFrom(this.brandService.getOne(conflictingBrandId));
      return {
        id: conflictingBrandId,
        description: brand.description || conflictingBrandDescription,
        shortDescription: brand.short_description || null,
        message: rawMessage
      };
    } catch {
      return {
        id: conflictingBrandId,
        description: conflictingBrandDescription,
        shortDescription: null,
        message: rawMessage
      };
    }
  }
}
